import {
    CustomerChangeName,
    formatAsFileSize,
    Identification,
    ChangeNameDocument,
    getChangeNameSMEEndpoint,
    SmeCustomerData,
    MarketError,
    FileInfo,
    editViewIdentificationSectionSmeFrontendSchema,
} from 'cpfr-common';
import { FileUpload, preventSubmit, Spinner, ValidatedInput } from '@cp-shared-19/frontend-ui';
import { Button, ButtonContainer, DataOverview, Fieldset, Form, Layout, Paragraph } from '@vwfs-bronson/bronson-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import { maxFileSize } from 'config';
import { CpDataApi } from 'cp-xhr';
import { SMEFormValues, initialValues } from './initialValues';
import { getSeparatedFilesIds, uploadFilesToMediaStorage } from './helpers';
import { EmailInputModal } from 'components/request/form-view/email-input/EmailInputModal';
import { getErrorCode, isDuplicateSubmissionError, useSubmitOnce } from 'utils';

export enum EditStatus {
    NOT_PERFORMED = 'NOT_PERFORMED',
    SUCCESS = 'SUCCESS',
    ERROR = 'ERROR',
    ERROR_VIRUS = 'ERROR_VIRUS',
    NO_CHANGE_DETECTED = 'NO_CHANGE_DETECTED',
    WRONG_REASON = 'WRONG_REASON',
    SIREN_IS_MANDATORY_FOR_SME = 'SIREN_IS_MANDATORY_FOR_SME',
    ERROR_NO_EMAIL = 'EMAIL_NOT_FOUND_OR_PROVIDED',
}

export type EditStatusWithData = {
    status: EditStatus;
    identification?: Identification;
    formValues?: CustomerChangeName;
};

export type EditSMEViewProps = {
    identification: Identification;
    cancelEditing: () => void;
    finishEditing: (newEditStatus: EditStatus, updatedIdentification?: Identification) => void;
    editStatus: EditStatus;
};

export const EditViewSME: React.FC<EditSMEViewProps> = ({ identification, cancelEditing, finishEditing }) => {
    const { t } = useTranslation(['my-profile', 'loader']);
    const { isSubmitting, run } = useSubmitOnce();
    const [error, setError] = useState<string | null>(null);
    const validationPrefix = 'identification.edit-view-sme';

    const fileUploadTranslationPrefix = 'identification.edit-view-sme.file-upload';
    initialValues.companyName = (identification.data as SmeCustomerData).companyName ?? '';

    // The latch covers the media storage upload too, not just the request: that
    // upload is where a second click used to slip through and send the change twice.
    const handleSubmit = (values: SMEFormValues): Promise<void> =>
        run(async () => {
            const { companyName: company, kbis, insee, registrationCertificate, email } = values;
            const { companyName, customerNumber, gender, isEligibleNpaiNotification } =
                identification.data as SmeCustomerData;

            const documentsValues = {
                kbis,
                insee,
                registrationCertificate,
            };

            const mediaStorageFilesIds: Promise<ChangeNameDocument>[] = uploadFilesToMediaStorage(documentsValues);

            const doAfterUploadError = (): void => {
                finishEditing(EditStatus.ERROR);
            };

            const reportSubmitted = (): void => {
                const updatedName: Identification = {
                    customerType: identification.customerType,
                    data: {
                        companyName: company,
                        customerNumber,
                        gender,
                        isEligibleNpaiNotification,
                        email: email || identification.data.email || '',
                    } as SmeCustomerData,
                };
                finishEditing(EditStatus.SUCCESS, updatedName);
            };

            const doAfterSuccessfullUpload = (documentsIds: FileInfo[]): Promise<void> => {
                const preparedChangeValues: CustomerChangeName = {
                    customerType: identification.customerType,
                    companyName: companyName === company ? '' : company,
                    documents: [...documentsIds],
                };

                return CpDataApi.put(getChangeNameSMEEndpoint(), preparedChangeValues)
                    .then(reportSubmitted)
                    .catch((error) => {
                        // A duplicate is not a failure: the change is already registered.
                        if (isDuplicateSubmissionError(error)) {
                            reportSubmitted();
                            return;
                        }
                        // Read through getErrorCode: a timeout or a network drop has no
                        // `response`, and reading into it here threw before the user was
                        // ever told the request failed.
                        const errorCode = getErrorCode(error);
                        setError(errorCode ?? null);
                        switch (errorCode) {
                            case MarketError.NO_CHANGE_DETECTED:
                                finishEditing(EditStatus.NO_CHANGE_DETECTED);
                                break;
                            case MarketError.MARKET_API_NOT_REACHABLE:
                                finishEditing(EditStatus.ERROR);
                                break;
                            case MarketError.WRONG_REASON:
                                finishEditing(EditStatus.WRONG_REASON);
                                break;
                            case MarketError.VIRUS:
                                finishEditing(EditStatus.ERROR_VIRUS);
                                break;
                            case MarketError.SIREN_IS_MANDATORY_FOR_SME:
                                finishEditing(EditStatus.SIREN_IS_MANDATORY_FOR_SME);
                                break;
                            case MarketError.ERROR_NO_EMAIL:
                                finishEditing(EditStatus.ERROR_NO_EMAIL);
                                break;
                            default:
                                finishEditing(EditStatus.ERROR);
                                break;
                        }
                    });
            };

            // Returned, so the latch is only released once the request answered.
            return Promise.all(mediaStorageFilesIds)
                .then((filesId: ChangeNameDocument[]) => {
                    const separatedDocumentsIds: FileInfo[] = getSeparatedFilesIds(filesId);
                    return doAfterSuccessfullUpload(separatedDocumentsIds);
                })
                .catch(() => {
                    doAfterUploadError();
                });
        });

    const getFileUploadsList = (): { name: string; suffix: string }[] => {
        return [
            { name: 'kbis', suffix: 'kbis' },
            { name: 'insee', suffix: 'insee' },
        ];
    };

    const errorMessages = {
        companyName: {
            required: t(`${validationPrefix}.validation-error.required-input`),
            matches: t(`${validationPrefix}.validation-error.no-numbers`),
        },
        kbis: {
            required: t(`${validationPrefix}.file-upload.validation.required`),
            max: t(`${validationPrefix}.file-upload.validation.wrong-number-of-files`),
        },
        insee: {
            required: t(`${validationPrefix}.file-upload.validation.required`),
            max: t(`${validationPrefix}.file-upload.validation.wrong-number-of-files`),
        },
    };

    return (
        <DataOverview titleLevel={2} title={t('identification.edit-view-sme.title')}>
            <Paragraph className={'u-mb'}>{t('identification.edit-view-sme.description')}</Paragraph>
            {isSubmitting && (
                <Spinner center>
                    <p>{t('genericText', { ns: 'loader' })}</p>
                </Spinner>
            )}
            <Formik
                initialValues={initialValues}
                validationSchema={editViewIdentificationSectionSmeFrontendSchema(errorMessages)}
                onSubmit={handleSubmit}
            >
                {({ handleSubmit, errors, isValid }) => (
                    <Form onSubmit={(e) => preventSubmit(e)} data-testid="edit-form">
                        <Fieldset>
                            <Fieldset.Row>
                                <Layout>
                                    <Layout.Item>
                                        <ValidatedInput
                                            className="c-form-field--no-floating"
                                            label={t('identification.edit-view-sme.company-name')}
                                            name="companyName"
                                            testId="companyName"
                                            inputId="company-name--my-profile-identification-section-sme"
                                            type="text"
                                            aria-required={true}
                                            isMandatory={true}
                                            requiredText=" *"
                                            autocomplete="organization"
                                        />
                                    </Layout.Item>
                                </Layout>
                            </Fieldset.Row>
                        </Fieldset>
                        <Fieldset.Row>
                            <Layout>
                                {getFileUploadsList().map(({ name, suffix }) => {
                                    return (
                                        <Layout.Item key={name}>
                                            <FileUpload
                                                name={name}
                                                uploadItemsLabel={''}
                                                descriptionText={t(`${fileUploadTranslationPrefix}.${suffix}`)}
                                                buttonText={t(`${fileUploadTranslationPrefix}.button`)}
                                                cancelLabel={t('translation:editable-section-nav.cancel')}
                                                fileSizeFormatter={(bytes: number): string =>
                                                    formatAsFileSize(bytes, 2)
                                                }
                                                maxFileSize={maxFileSize}
                                                multiple={false}
                                                validFileFormats={['pdf']}
                                                sizeError={t(`${fileUploadTranslationPrefix}.validation.wrong-size`)}
                                                typeError={t(`${fileUploadTranslationPrefix}.validation.wrong-type`)}
                                                sizeAndTypeError={
                                                    <>
                                                        {t(`${fileUploadTranslationPrefix}.validation.wrong-size`)}
                                                        <br aria-hidden="true" />
                                                        {t(`${fileUploadTranslationPrefix}.validation.wrong-type`)}
                                                    </>
                                                }
                                            />
                                        </Layout.Item>
                                    );
                                })}
                            </Layout>
                        </Fieldset.Row>
                        <Fieldset>
                            <Fieldset.Row>
                                <ButtonContainer center>
                                    <Button secondary onClick={cancelEditing} testId="cancel-button" type="button">
                                        {t('identification.edit-view-sme.button.cancel')}
                                    </Button>
                                    {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                                    <Button
                                        onClick={() => handleSubmit()}
                                        testId="submit-button"
                                        type="button"
                                        disabled={isSubmitting || Object.keys(errors).length > 0 || !isValid}
                                    >
                                        {t('identification.edit-view-sme.button.confirm')}
                                    </Button>
                                </ButtonContainer>
                            </Fieldset.Row>
                        </Fieldset>
                        <EmailInputModal
                            show={error === 'EMAIL_NOT_FOUND_OR_PROVIDED'}
                            onClose={() => setError(null)}
                            onConfirm={() => handleSubmit()}
                        />
                    </Form>
                )}
            </Formik>
        </DataOverview>
    );
};
